import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import API from '../utils/api';

export default function ChecklistExecute({ user }) {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');

  const [recordId, setRecordId] = useState(null);
  const [items, setItems] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    const start = async () => {
      try {
        const r = await API.post('/checklist/records/start', {
          daily_task_id: parseInt(taskId),
          template_id: parseInt(templateId)
        });
        setRecordId(r.data.record_id);
        setItems(r.data.items);
        // 获取模板名
        const tr = await API.get(`/checklist/templates/${templateId}`);
        setTemplateName(tr.data.name);
      } catch (err) {
        alert(err.response?.data?.message || '加载失败');
        navigate(-1);
      } finally { setLoading(false); }
    };
    start();
  }, [taskId, templateId]);

  // 获取手机真实GPS位置
  const getGpsLocation = () => new Promise(resolve => {
    if (!navigator.geolocation || !window.isSecureContext) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      p => resolve({
        lat: p.coords.latitude,
        lng: p.coords.longitude
      }),
      () => resolve(null),
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0
      }
    );
  });

  // 巡检照片写入真实时间、地点、人员、GPS水印
  const watermarkChecklistPhoto = async (file, item) => {
    const gps = await getGpsLocation();

    const url = URL.createObjectURL(file);

    const photo = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

    // 最大边限制1600px，避免照片太大
    const maxSide = 1600;
    const scale = Math.min(
      1,
      maxSide / Math.max(photo.naturalWidth, photo.naturalHeight)
    );

    const width = Math.max(
      1,
      Math.round(photo.naturalWidth * scale)
    );

    const height = Math.max(
      1,
      Math.round(photo.naturalHeight * scale)
    );

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(photo, 0, 0, width, height);

    URL.revokeObjectURL(url);

    const now = new Date();

    const timeText =
      now.getFullYear() + '-' +
      String(now.getMonth() + 1).padStart(2, '0') + '-' +
      String(now.getDate()).padStart(2, '0') + ' ' +
      String(now.getHours()).padStart(2, '0') + ':' +
      String(now.getMinutes()).padStart(2, '0') + ':' +
      String(now.getSeconds()).padStart(2, '0');

    const executorName =
      user?.real_name ||
      user?.name ||
      user?.username ||
      '当前员工';

    const lines = [
      `拍摄时间：${timeText}`,
      '项目位置：永嘉县 · 大诚瓯江印象',
      `巡检位置：${templateName || '巡检现场'}`,
      `巡检项目：${item?.item_name || '巡检项目'}`,
      `执行人员：${executorName}`
    ];

    if (gps) {
      lines.push(
        `GPS：${gps.lat.toFixed(6)}, ${gps.lng.toFixed(6)}`
      );
    } else {
      lines.push('GPS：定位失败');
    }

    const fontSize = Math.max(
      22,
      Math.round(width * 0.025)
    );

    const lineHeight = Math.round(fontSize * 1.45);
    const padding = Math.max(
      18,
      Math.round(width * 0.022)
    );

    const boxHeight =
      padding * 2 +
      lineHeight * lines.length;

    // 半透明黑底
    ctx.fillStyle = 'rgba(0,0,0,0.58)';
    ctx.fillRect(
      0,
      height - boxHeight,
      width,
      boxHeight
    );

    ctx.fillStyle = '#fff';
    ctx.font =
      `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif`;

    ctx.textBaseline = 'top';

    ctx.shadowColor = 'rgba(0,0,0,.65)';
    ctx.shadowBlur = 3;

    lines.forEach((line, index) => {
      ctx.fillText(
        line,
        padding,
        height - boxHeight + padding + index * lineHeight,
        width - padding * 2
      );
    });

    const blob = await new Promise(resolve =>
      canvas.toBlob(
        resolve,
        'image/jpeg',
        0.82
      )
    );

    if (!blob) {
      throw new Error('巡检照片水印生成失败');
    }

    return new File(
      [blob],
      `checklist-${Date.now()}.jpg`,
      {
        type: 'image/jpeg',
        lastModified: Date.now()
      }
    );
  };

  const handleResult = async (item, isOk, file) => {
    setUploading(u => ({ ...u, [item.id]: true }));
    try {
      const fd = new FormData();
      fd.append('is_ok', isOk ? 1 : 0);
      if (file) fd.append('image', file);
      const r = await API.post(
        `/checklist/records/${recordId}/items/${item.id}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setItems(prev => prev.map(i => i.id === item.id
        ? { ...i, is_ok: isOk ? 1 : 0, image_path: r.data.image_path || i.image_path }
        : i
      ));
    } catch { alert('保存失败，请重试'); }
    finally { setUploading(u => ({ ...u, [item.id]: false })); }
  };

  const handlePhoto = (item, isOk) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';

    input.onchange = async e => {
      const file = e.target.files && e.target.files[0];

      if (!file) return;

      setUploading(u => ({
        ...u,
        [item.id]: true
      }));

      try {
        const stampedFile =
          await watermarkChecklistPhoto(file, item);

        await handleResult(
          item,
          isOk,
          stampedFile
        );
      } catch (err) {
        console.error(err);
        alert(
          err.message ||
          '照片水印生成失败，请重新拍照'
        );

        setUploading(u => ({
          ...u,
          [item.id]: false
        }));
      }
    };

    input.click();
  };

  const handleSubmit = async () => {
    const unfinished = items.filter(i => i.is_ok === null || i.is_ok === undefined);
    if (unfinished.length > 0) {
      alert(`还有 ${unfinished.length} 个项目未处理，请全部完成后提交`);
      return;
    }
    if (!window.confirm('确认提交巡检结果？')) return;
    setSubmitting(true);
    try {
      const r = await API.post(`/checklist/records/${recordId}/submit`);
      const bad = r.data.auto_tasks_created;
      alert(`巡检提交成功！${bad > 0 ? `已自动生成 ${bad} 条异常代办事项。` : '所有项目正常！'}`);
      navigate(-1);
    } catch (err) {
      alert(err.response?.data?.message || '提交失败');
    } finally { setSubmitting(false); }
  };

  const done = items.filter(i => i.is_ok !== null && i.is_ok !== undefined).length;
  const total = items.length;
  const allDone = done === total && total > 0;

  if (loading) return <div style={s.loading}>加载中...</div>;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => navigate(-1)}>← 返回</button>
        <div style={s.headerRight}>
          <div style={s.title}>🔍 {templateName}</div>
          <div style={s.progress}>{done}/{total} 项已完成</div>
        </div>
      </div>

      <div style={s.progressBar}>
        <div style={{...s.progressFill, width: total > 0 ? `${(done/total)*100}%` : '0%'}} />
      </div>

      <div style={s.list}>
        {items.map((item, idx) => {
          const isDone = item.is_ok !== null && item.is_ok !== undefined;
          const isOk = item.is_ok === 1;
          const isLoading = uploading[item.id];
          return (
            <div key={item.id} style={{...s.card, borderLeftColor: isDone ? (isOk ? '#52c41a' : '#ff4d4f') : '#d9d9d9'}}>
              <div style={s.itemHeader}>
                <span style={s.itemNo}>{idx + 1}</span>
                <span style={s.itemName}>{item.item_name}</span>
                {isDone && (
                  <span style={{...s.statusTag, background: isOk ? '#f6ffed' : '#fff2f0', color: isOk ? '#52c41a' : '#ff4d4f', border: `1px solid ${isOk ? '#b7eb8f' : '#ffa39e'}`}}>
                    {isOk ? '✓ 正常' : '✗ 异常'}
                  </span>
                )}
              </div>
              {item.image_path && (
                <img src={`/uploads/${item.image_path}`} style={s.thumb}
                  alt="" onClick={() => window.open(`/uploads/${item.image_path}`)} />
              )}
              {isLoading ? (
                <div style={s.loadingRow}>上传中...</div>
              ) : (
                <div style={s.btnRow}>
                  <button style={{...s.okBtn, opacity: isDone && isOk ? 1 : 0.6}}
                    onClick={() => handlePhoto(item, true)}>
                    📷 正常
                  </button>
                  <button style={{...s.badBtn, opacity: isDone && !isOk ? 1 : 0.6}}
                    onClick={() => handlePhoto(item, false)}>
                    📷 不正常
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={s.footer}>
        <div style={s.footerInfo}>
          {allDone ? '✅ 所有项目已完成，可以提交' : `还有 ${total - done} 项未处理`}
        </div>
        <button style={{...s.submitBtn, opacity: allDone ? 1 : 0.5}}
          onClick={handleSubmit} disabled={!allDone || submitting}>
          {submitting ? '提交中...' : '提交巡检结果'}
        </button>
      </div>
    </div>
  );
}

const s = {
  container: { minHeight:'100vh', background:'#f5f7fa', paddingBottom:'210px' },
  loading: { display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'#999' },
  header: { background:'linear-gradient(90deg,#1a6a3a,#2d9d5a)', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', position:'sticky', top:0, zIndex:100 },
  backBtn: { background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', borderRadius:'8px', padding:'6px 12px', fontSize:'14px', cursor:'pointer', whiteSpace:'nowrap' },
  headerRight: { flex:1 },
  title: { color:'#fff', fontSize:'16px', fontWeight:700 },
  progress: { color:'rgba(255,255,255,0.85)', fontSize:'13px', marginTop:'2px' },
  progressBar: { height:'4px', background:'#e0e0e0' },
  progressFill: { height:'4px', background:'#52c41a', transition:'width 0.3s' },
  list: { padding:'12px' },
  card: { background:'#fff', borderRadius:'10px', padding:'14px', marginBottom:'10px', borderLeft:'4px solid #d9d9d9', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' },
  itemHeader: { display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' },
  itemNo: { background:'#f0f0f0', color:'#666', borderRadius:'50%', width:'24px', height:'24px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:700, flexShrink:0 },
  itemName: { flex:1, fontSize:'15px', fontWeight:600, color:'#333' },
  statusTag: { padding:'2px 10px', borderRadius:'10px', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap' },
  thumb: { width:'80px', height:'80px', objectFit:'cover', borderRadius:'8px', marginBottom:'10px', cursor:'pointer' },
  loadingRow: { textAlign:'center', color:'#1890ff', padding:'8px', fontSize:'14px' },
  btnRow: { display:'flex', gap:'10px' },
  okBtn: { flex:1, padding:'10px', border:'2px solid #52c41a', borderRadius:'8px', background:'#f6ffed', color:'#389e0d', fontSize:'14px', fontWeight:600, cursor:'pointer' },
  badBtn: { flex:1, padding:'10px', border:'2px solid #ff4d4f', borderRadius:'8px', background:'#fff2f0', color:'#cf1322', fontSize:'14px', fontWeight:600, cursor:'pointer' },
  footer: { position:'fixed', bottom:'64px', left:0, right:0, zIndex:150, background:'#fff', padding:'10px 16px 12px', boxShadow:'0 -3px 12px rgba(0,0,0,0.16)', borderTop:'1px solid #e8e8e8' },
  footerInfo: { textAlign:'center', fontSize:'13px', color:'#888', marginBottom:'8px' },
  submitBtn: { width:'100%', padding:'13px', border:'none', borderRadius:'10px', background:'#1a6a3a', color:'#fff', fontSize:'16px', fontWeight:700, cursor:'pointer' },
};
